import { MAX_DIAGNOSTIC_DURATION_MS } from '../../shared/diagnostics';
import type { Phase, StateMessage } from '../../shared/protocol';
import type { SnapshotState } from './room-session-state';

// The countdown snapshot already carries the board, so the browser opens play itself at
// `startAt` instead of waiting for the server's `playing` snapshot, which a slow or dropped
// socket can delay by seconds.

/**
 * Beyond clock-offset error and one network trip: the server's confirmation of play was late,
 * or lost and delivered only by a reconnect, while words entered meanwhile waited to be sent.
 */
export const ROUND_START_LATE_MS = 1_000;

/** Milliseconds until this browser should open play, or null when no countdown is showing. */
export function roundStartDelay(
  snapshot: Pick<SnapshotState, 'phase' | 'startAt'>,
  serverNow: number,
): number | null {
  if (snapshot.phase !== 'countdown') return null;
  return Math.max(0, snapshot.startAt - serverNow);
}

/** Open play for the countdown that reached `startAt`; false when the snapshot moved on meanwhile. */
export function startRoundLocally(snapshot: Pick<SnapshotState, 'phase'>): boolean {
  if (snapshot.phase !== 'countdown') return false;
  snapshot.phase = 'playing';
  return true;
}

/**
 * How long past `startAt` the server's `playing` snapshot arrived for the countdown it last
 * sent this browser, or null when the snapshot is not that transition or arrived in time.
 */
export function roundStartLateness(
  previous: { serverPhase: Phase; roundId: string | null },
  message: Pick<StateMessage, 'phase' | 'currentRoundId' | 'startAt'>,
  serverNow: number,
): number | null {
  if (previous.serverPhase !== 'countdown' || message.phase !== 'playing') return null;
  if (message.currentRoundId === null || message.currentRoundId !== previous.roundId) return null;
  const late = Math.round(serverNow - message.startAt);
  return late >= ROUND_START_LATE_MS ? Math.min(MAX_DIAGNOSTIC_DURATION_MS, late) : null;
}
